import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePresenterSync, openAudienceWindow } from './usePresenterSync';
import type { Deck, Theme } from './types';

/** Scale an iframe (1280×720 native) to fit its parent container. */
function useIframeScale(containerRef: React.RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const iframe = el.querySelector('iframe') as HTMLIFrameElement | null;
    if (!iframe) return;

    function scale() {
      if (!el || !iframe) return;
      const rect = el.getBoundingClientRect();
      const s = Math.min(rect.width / 1280, rect.height / 720);
      iframe.style.transform = `scale(${s})`;
      iframe.style.transformOrigin = '0 0';
      iframe.style.width = '1280px';
      iframe.style.height = '720px';
    }
    scale();
    const observer = new ResizeObserver(scale);
    observer.observe(el);
    return () => observer.disconnect();
  });
}

interface Props {
  deck: Deck;
}

interface SlideInfo {
  id: number;
  section: string;
  theme: Theme;
  notes: string[];
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function PresenterLayout({ deck }: Props) {
  const { slides } = deck;
  const total = slides.length;

  const { idx, go } = usePresenterSync(deck.slug, 'presenter', 0);
  const [slideInfo, setSlideInfo] = useState<SlideInfo | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const nextIframeRef = useRef<HTMLIFrameElement>(null);
  const currentFrameContainerRef = useRef<HTMLDivElement>(null);
  const nextFrameContainerRef = useRef<HTMLDivElement>(null);

  useIframeScale(currentFrameContainerRef);
  useIframeScale(nextFrameContainerRef);

  // Timer
  useEffect(() => {
    if (!timerRunning) return;
    const interval = setInterval(() => setElapsed(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [timerRunning]);

  // Receive slide info from current-slide iframe
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'sn-slide-info') return;
      const p = e.data.payload;
      setSlideInfo({
        id: p.id,
        section: p.section,
        theme: p.theme,
        notes: [...p.notes],
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Navigate the current-slide iframe when idx changes
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'sn-nav', idx },
      window.location.origin,
    );
  }, [idx]);

  // Navigate the next-slide iframe
  useEffect(() => {
    nextIframeRef.current?.contentWindow?.postMessage(
      { type: 'sn-nav', idx: idx + 1 },
      window.location.origin,
    );
  }, [idx]);

  // Keyboard navigation
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          go(Math.min(total - 1, idx + 1));
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          go(Math.max(0, idx - 1));
          break;
        case 'Home':
          go(0);
          break;
        case 'End':
          go(total - 1);
          break;
        default:
          if (e.key >= '1' && e.key <= '9') {
            go(Math.min(total - 1, parseInt(e.key, 10) - 1));
          }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, idx, total]);

  const handleOpenAudience = useCallback(() => {
    const w = openAudienceWindow(deck.slug);
    if (w) {
      setAudienceOpen(true);
      setTimerRunning(true);
    }
  }, [deck.slug]);

  const handlePrint = useCallback(() => {
    // Open the deck in a new window and trigger print
    const printWin = window.open(`/${deck.slug}`, '_blank');
    if (printWin) {
      printWin.addEventListener('load', () => {
        setTimeout(() => printWin.print(), 500);
      });
    }
  }, [deck.slug]);

  const previewUrl = `/${deck.slug}?preview=1#1`;

  const currentSlide = slides[idx];
  const nextSlide = idx + 1 < total ? slides[idx + 1] : null;

  return (
    <div className={`presenter-layout presenter-layout--${deck.slug}`}>
      {/* Top bar */}
      <header className="presenter-topbar">
        <div className="presenter-topbar-left">
          <img src="/assets/logomark-blue.svg" alt="" className="presenter-logo" />
          <span className="presenter-deck-name">{deck.title}</span>
        </div>
        <div className="presenter-topbar-mid">
          <span className="presenter-slide-num">
            {String(idx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </span>
          {slideInfo && (
            <span className="presenter-section">{slideInfo.section}</span>
          )}
        </div>
        <div className="presenter-topbar-right">
          <div className="presenter-timer" onClick={() => setTimerRunning(r => !r)}>
            <span className={`presenter-timer-dot ${timerRunning ? 'is-running' : ''}`} />
            {formatTime(elapsed)}
          </div>
          <button type="button" className="presenter-btn" onClick={() => setElapsed(0)}>
            Reset
          </button>
          <button type="button" className="presenter-btn" onClick={handlePrint}>
            PDF
          </button>
          {!audienceOpen && (
            <button type="button" className="presenter-btn presenter-btn-primary" onClick={handleOpenAudience}>
              Open Audience View
            </button>
          )}
        </div>
      </header>

      {/* Main body */}
      <div className="presenter-body">
        {/* Speaker notes (large left pane) */}
        <div className="presenter-notes">
          <div className="presenter-frame-label">Speaker Notes</div>
          <div className="presenter-notes-body">
            {currentSlide.notes.length > 0 ? (
              <ul className="presenter-notes-list">
                {currentSlide.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            ) : (
              <p className="presenter-notes-empty">No notes for this slide.</p>
            )}
          </div>
        </div>

        {/* Right sidebar: current slide on top, next slide below */}
        <div className="presenter-sidebar">
          {/* Current slide */}
          <div className="presenter-current">
            <div className="presenter-frame-label">Current Slide</div>
            <div className="presenter-frame presenter-frame-current" ref={currentFrameContainerRef}>
              <iframe
                ref={iframeRef}
                src={previewUrl}
                title="Current slide"
                className="presenter-iframe"
              />
            </div>
          </div>

          {/* Next slide preview */}
          <div className="presenter-next">
            <div className="presenter-frame-label">
              {nextSlide ? 'Next Slide' : 'End of Deck'}
            </div>
            <div className="presenter-frame presenter-frame-next" ref={nextFrameContainerRef}>
              {nextSlide ? (
                <iframe
                  ref={nextIframeRef}
                  src={`/${deck.slug}?preview=1#2`}
                  title="Next slide"
                  className="presenter-iframe"
                />
              ) : (
                <div className="presenter-end-card">
                  <span>No more slides</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard help */}
      <div className="presenter-help">
        <kbd>←</kbd><kbd>→</kbd> navigate
        <span className="presenter-help-sep">·</span>
        Click timer to pause/resume
      </div>
    </div>
  );
}
